import { Injectable, OnDestroy } from '@angular/core';
import { md5 } from 'js-md5';
import { BroadcasterService } from 'src/app/shared/services/broadcaster.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { CompanyApiService } from 'src/app/api/services/company-api.service';
import { RiseFont } from '../text/text.component';

@Injectable({
  providedIn: 'root',
})
export class CustomFontListManagementService implements OnDestroy {
  private subscription: any;
  private customFonts: RiseFont[] = [];
  private companyId: string; // compoany that fonts are loaded for
  public onFontAddedHandler: () => void;
  public onFontSelectedHandler: (font: RiseFont) => void;

  constructor(
    private companyStateService: CompanyStateService,
    private companyApiService: CompanyApiService,
    private broadcaster: BroadcasterService,
  ) {
    this.subscription = this.broadcaster.on(
      'risevision.company.selectedCompanyChanged',
      () => {
        this._loadCustomFonts(true);
      },
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  getFonts(): Array<RiseFont> {
    this._loadCustomFonts();
    return this.customFonts;
  }

  async addFont(fontUrl: string, fontName?: string): Promise<boolean> {
    try {
      if (!fontUrl) {
        return false;
      }

      if (!fontName) {
        fontName = this._getFontNameFromUrl(fontUrl);
      }

      if (this.customFonts.some((font) => font.url === fontUrl)) {
        return false;
      }

      //make sure font ID starts with letter so we don't need to wrap it in quotes when used as font-family
      const fontId = 'F' + md5(fontUrl);

      this.customFonts.push({ url: fontUrl, name: fontName, font: fontId });
      await this._updateCompanySettings();
      if (this.onFontAddedHandler) {
        this.onFontAddedHandler();
      }
      return true;
    } catch (err) {
      console.error('Failed to add font:', err);
      return false;
    }
  }

  async removeFont(fontUrl: string): Promise<boolean> {
    try {
      const updatedFonts = this.customFonts.filter(
        (font) => font.url !== fontUrl,
      );

      if (updatedFonts.length === this.customFonts.length) {
        return false;
      }

      this.customFonts = updatedFonts;

      await this._updateCompanySettings();
      return true;
    } catch (err) {
      console.error('Failed to remove font:', err);
      return false;
    }
  }

  selectFont(fontUrl: string) {
    const font = this.customFonts.find((font) => font.url === fontUrl);
    if (font && this.onFontSelectedHandler) {
      this.onFontSelectedHandler(font);
    }
  }

  _loadCustomFonts(forceRefresh?) {
    const selectedCompanyChanged =
      this.companyId !== this.companyStateService.getSelectedCompanyId();

    if (!this.customFonts || forceRefresh || selectedCompanyChanged) {
      this.companyId = this.companyStateService.getSelectedCompanyId();
      const company = this.companyStateService.getCopyOfSelectedCompany();
      const settings = company.settings || {};

      try {
        this.customFonts = settings.customFonts
          ? JSON.parse(settings.customFonts)
          : [];
      } catch (error) {
        console.error('Failed to parse custom fonts:', error);
        this.customFonts = [];
      }
    }
  }

  private async _updateCompanySettings() {
    const companyPatch = {
      settings: {
        customFonts: JSON.stringify(this.customFonts),
      },
    };
    const updatedCompany = await this.companyApiService.updateCompany(
      this.companyStateService.getSelectedCompanyId(),
      companyPatch,
    );
    this.companyStateService.updateCompanySettings(updatedCompany);
  }

  private _getFontNameFromUrl(url: string): string {
    const urlParts = url.split('/');
    const fileName = urlParts[urlParts.length - 1];
    return fileName;
  }
}
